import api from '../services/api';
import { environment, getBackendURL } from '../config/environment';

// Interfaz para el estado de salud del backend
export interface BackendHealthStatus {
  isConnected: boolean;
  responseTime: number;
  status: string;
  error?: string;
  timestamp: Date;
  statusCode?: number;
}

// Función para verificar la salud del backend
export const checkBackendHealth = async (): Promise<BackendHealthStatus> => {
  const startTime = Date.now();
  
  try {
    // Intentar hacer una petición simple al backend - usar login que puede no requerir auth
    const response = await api.get('login');
    const responseTime = Date.now() - startTime;
    
    return {
      isConnected: true,
      responseTime,
      status: 'OK',
      statusCode: response.status,
      timestamp: new Date(),
    };
  } catch (error: any) {
    const responseTime = Date.now() - startTime;
    
    // Si es error 403, el backend está funcionando pero requiere autenticación
    if (error.response?.status === 403) {
      return {
        isConnected: true,
        responseTime,
        status: 'AUTH_REQUIRED',
        statusCode: 403,
        error: 'Backend funcionando pero requiere autenticación',
        timestamp: new Date(),
      };
    }
    
    // Si es error 401, también indica que el backend está funcionando
    if (error.response?.status === 401) {
      return {
        isConnected: true,
        responseTime,
        status: 'UNAUTHORIZED',
        statusCode: 401,
        error: 'Backend funcionando pero no autorizado',
        timestamp: new Date(),
      };
    }

    // Si es error 400, también indica que el backend está funcionando
    if (error.response?.status === 400) {
      return {
        isConnected: true,
        responseTime,
        status: 'BAD_REQUEST',
        statusCode: 400,
        error: 'Backend funcionando pero datos inválidos',
        timestamp: new Date(),
      };
    }
    
    return {
      isConnected: false,
      responseTime,
      status: 'ERROR',
      statusCode: error.response?.status,
      error: error.message || 'Error desconocido',
      timestamp: new Date(),
    };
  }
};

// Función para diagnosticar problemas de conectividad
export const diagnoseBackendIssues = async (): Promise<string[]> => {
  const issues: string[] = [];
  
  try {
    // Verificar si el backend responde
    const health = await checkBackendHealth();
    
    if (health.isConnected) {
      if (health.status === 'OK') {
        console.log(`✅ Backend conectado exitosamente - Tiempo de respuesta: ${health.responseTime}ms`);
      } else if (health.status === 'AUTH_REQUIRED') {
        console.log(`✅ Backend conectado - Requiere autenticación (403)`);
      } else if (health.status === 'UNAUTHORIZED') {
        console.log(`✅ Backend conectado - No autorizado (401)`);
      }
    } else {
      issues.push(`❌ Backend no responde: ${health.error}`);
    }
    
    // Verificar URL del backend (efectiva)
    console.log(`🌐 URL del backend: ${getBackendURL()}`);
    
    // Verificar timeout
    if (health.responseTime > environment.api.timeout) {
      issues.push(`⚠️ Tiempo de respuesta lento: ${health.responseTime}ms`);
    }
    
  } catch (error: any) {
    issues.push(`❌ Error al diagnosticar: ${error.message}`);
  }
  
  return issues;
};

// Función para mostrar información de conectividad en consola
export const logBackendInfo = () => {
  console.log('🔍 Información del Backend:');
  console.log(`   URL: ${getBackendURL()}`);
  console.log(`   Timeout: ${environment.api.timeout}ms`);
  console.log(`   Entorno: ${environment.app.environment}`);
  console.log(`   Aplicación: ${environment.app.name} v${environment.app.version}`);
};

// Función para verificar endpoints específicos
export const checkSpecificEndpoints = async (): Promise<Record<string, boolean>> => {
  const endpoints = [
    'login',
    'frase_motivacional',
    'usuario/mi_informacion',
    'punto_servicio/todos'
  ];
  
  const results: Record<string, boolean> = {};
  
  for (const endpoint of endpoints) {
    try {
      await api.get(endpoint);
      results[endpoint] = true;
    } catch (error: any) {
      // Considerar 403 y 401 como "funcionando" ya que el backend responde
      results[endpoint] = error.response?.status === 403 || error.response?.status === 401;
    }
  }
  
  return results;
};

// Función para verificar conectividad básica (sin autenticación)
export const checkBasicConnectivity = async (): Promise<boolean> => {
  try {
    const response = await fetch(getBackendURL(), {
      method: 'HEAD',
      mode: 'cors'
    });
    return response.ok || response.status === 403 || response.status === 401;
  } catch (error) {
    console.error('Error en conectividad básica:', error);
    return false;
  }
}; 